import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { createNotification } from '@/lib/notifications';
import {
  linkProjectFileToDailyLog,
  DAILY_LOG_SYNC_EVENT,
  emitDailyLogSync,
  loadDailyLogPhotos,
  loadSubcontractorDailyLogs,
  type DailyLogEntryRecord,
} from '@/lib/dailyLogUtils';

export interface DailyLogPhoto {
  id: string;
  project_id: string;
  file_name: string;
  file_url: string;
  file_type: string | null;
  file_size: string | null;
  log_date: string | null;
  caption: string | null;
  stage_id: string | null;
  stage_name: string | null;
  review_status: string | null;
  is_client_visible: boolean;
  is_visible: boolean;
  note: string | null;
  created_at: string;
  uploaded_by: string | null;
  daily_log_id?: string | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
}

export interface DaySummary {
  date: string;
  count: number;
  photos: DailyLogPhoto[];
  logCount: number;
  logs: DailyLogEntryRecord[];
  hasSubmittedLog: boolean;
}

interface UseDailyLogOptions {
  /** When set, also load jphph_daily_logs rows created by this user (subcontractor calendar). */
  userId?: string | null;
}

function mergeDaySummaries(
  photos: DailyLogPhoto[],
  logs: DailyLogEntryRecord[]
): Record<string, DaySummary> {
  const summaries: Record<string, DaySummary> = {};

  photos.forEach((p) => {
    const d = p.log_date || p.created_at.split('T')[0];
    if (!summaries[d]) {
      summaries[d] = { date: d, count: 0, photos: [], logCount: 0, logs: [], hasSubmittedLog: false };
    }
    summaries[d].count += 1;
    summaries[d].photos.push(p);
  });

  logs.forEach((log) => {
    const d = log.log_date;
    if (!d) return;
    if (!summaries[d]) {
      summaries[d] = { date: d, count: 0, photos: [], logCount: 0, logs: [], hasSubmittedLog: false };
    }
    summaries[d].logs.push(log);
    summaries[d].logCount = summaries[d].logs.length;
    summaries[d].hasSubmittedLog = summaries[d].logCount > 0;
  });

  return summaries;
}

export function useDailyLog(projectIds: string[], options: UseDailyLogOptions = {}) {
  const { userId } = options;
  const [photos, setPhotos] = useState<DailyLogPhoto[]>([]);
  const [dailyLogs, setDailyLogs] = useState<DailyLogEntryRecord[]>([]);
  const [daySummaries, setDaySummaries] = useState<Record<string, DaySummary>>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadPhotos = useCallback(async () => {
    if (projectIds.length === 0) {
      setPhotos([]);
      setDailyLogs([]);
      setDaySummaries({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const errors: string[] = [];

      const { data, error: err } = await supabase
        .from('jphph_project_files')
        .select(
          'id, project_id, file_name, file_url, file_type, file_size, log_date, caption, stage_id, review_status, is_client_visible, is_visible, note, created_at, uploaded_by, daily_log_id, storage_bucket, storage_path'
        )
        .in('project_id', projectIds)
        .eq('is_deleted', false)
        .eq('uploaded_context', 'daily_log')
        .eq('is_visible', true)
        .or(
          'file_type.in.(jpg,jpeg,png,webp,heic,heif,gif,bmp,tiff,avif),file_type.is.null'
        )
        .order('log_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (err) throw err;

      let records = (data ?? []) as DailyLogPhoto[];

      if (userId) {
        records = records.filter((p) => p.uploaded_by === userId);
      }

      let logList: DailyLogEntryRecord[] = [];
      if (userId) {
        const logResult = await loadSubcontractorDailyLogs(projectIds, userId);
        if (logResult.error) {
          errors.push(`Daily log entries: ${logResult.error}`);
        } else {
          logList = logResult.logs;
        }

        if (logList.length > 0) {
          const photosMap = await loadDailyLogPhotos(logList.map((l) => l.id));
          const existingIds = new Set(records.map((r) => r.id));
          const logDateById = new Map(logList.map((l) => [l.id, l.log_date]));

          for (const log of logList) {
            const linked = photosMap[log.id] || [];
            for (const lp of linked) {
              const fileId = lp.source_photo_id || lp.id;
              if (existingIds.has(fileId)) continue;
              existingIds.add(fileId);
              records.push({
                id: fileId,
                project_id: log.project_id,
                file_name: 'daily-log-photo',
                file_url: lp.photo_url,
                file_type: 'jpg',
                file_size: null,
                log_date: logDateById.get(log.id) || null,
                caption: lp.caption,
                stage_id: null,
                stage_name: null,
                review_status: 'pending_management_review',
                is_client_visible: false,
                is_visible: true,
                note: lp.caption,
                created_at: log.created_at,
                uploaded_by: userId,
                daily_log_id: log.id,
                storage_bucket: lp.storage_bucket ?? null,
                storage_path: lp.storage_path ?? null,
              });
            }
          }
        }
      }

      const stageIds = Array.from(new Set(records.map((r) => r.stage_id).filter(Boolean))) as string[];
      let stageMap: Record<string, string> = {};
      if (stageIds.length > 0) {
        const { data: stageRows } = await supabase
          .from('jphph_project_stages')
          .select('id, stage_name')
          .in('id', stageIds);
        (stageRows ?? []).forEach((s: { id: string; stage_name: string }) => {
          stageMap[s.id] = s.stage_name;
        });
      }

      const enriched = records.map((r) => ({
        ...r,
        stage_name: r.stage_id ? stageMap[r.stage_id] || null : null,
      }));

      if (userId) {
        enriched.sort((a, b) => {
          const dateA = a.log_date || a.created_at;
          const dateB = b.log_date || b.created_at;
          return dateB.localeCompare(dateA);
        });
      }

      setPhotos(enriched);
      setDailyLogs(logList);
      setDaySummaries(mergeDaySummaries(enriched, logList));

      if (errors.length > 0) {
        setError(errors.join('; '));
      }

      if (import.meta.env.DEV) {
        console.log('[useDailyLog] loaded', {
          projectIds,
          userId: userId || null,
          totalPhotos: enriched.length,
          totalLogs: logList.length,
          daysWithData: Object.keys(mergeDaySummaries(enriched, logList)).length,
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [projectIds, userId]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ projectId?: string }>).detail;
      if (detail?.projectId && projectIds.length > 0 && !projectIds.includes(detail.projectId)) {
        return;
      }
      loadPhotos();
    };
    window.addEventListener(DAILY_LOG_SYNC_EVENT, handler);
    return () => window.removeEventListener(DAILY_LOG_SYNC_EVENT, handler);
  }, [loadPhotos, projectIds]);

  const refresh = useCallback(() => {
    loadPhotos();
  }, [loadPhotos]);

  return { photos, dailyLogs, daySummaries, loading, error, refresh };
}

export async function uploadDailyLogPhoto(
  file: File,
  projectId: string,
  userId: string,
  logDate: string,
  options?: { stageId?: string | null; caption?: string; dailyLogId?: string | null }
): Promise<{ photo: DailyLogPhoto | null; error?: string }> {
  // Defensive: project_id must be a valid UUID. Some edge cases pass a composite stage id
  // like "uuid::StageName". Strip the suffix and validate.
  const cleanProjectId = projectId.includes('::') ? projectId.split('::')[0] : projectId;
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!UUID_RE.test(cleanProjectId)) {
    console.error('[DailyLog] Invalid projectId passed to uploadDailyLogPhoto:', { raw: projectId, clean: cleanProjectId });
    return { photo: null, error: `Invalid project ID: "${projectId}". Cannot save photo.` };
  }

  // Resolve stage_id: may be a real UUID, or a composite "projectId::stageName"
  let cleanStageId: string | null = options?.stageId || null;
  if (cleanStageId && cleanStageId.includes('::')) {
    // Composite fallback ID — try to resolve to a real project stage UUID
    const stageName = cleanStageId.split('::')[1];
    const { data: stageRow } = await supabase
      .from('jphph_project_stages')
      .select('id')
      .eq('project_id', cleanProjectId)
      .eq('stage_name', stageName)
      .maybeSingle();
    if (stageRow && UUID_RE.test(stageRow.id)) {
      cleanStageId = stageRow.id;
    } else {
      cleanStageId = null;
    }
  }
  if (cleanStageId && !UUID_RE.test(cleanStageId)) {
    console.warn('[DailyLog] Invalid stageId after resolution — will save without stage:', {
      rawStageId: options?.stageId,
      resolved: cleanStageId,
      projectId: cleanProjectId,
    });
    cleanStageId = null;
  }

  const rawExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  // Normalize HEIC/HEIF to jpeg for broader compatibility
  const ext = rawExt === 'heic' || rawExt === 'heif' ? 'jpeg' : rawExt;
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  // Use dedicated daily-log-photos bucket with organized path
  const bucket = 'daily-log-photos';
  const path = `daily-log-photos/${cleanProjectId}/${logDate}/${Date.now()}_${safeName}`;

  const { error: storageError } = await supabase.storage
    .from(bucket)
    .upload(path, file, { upsert: false });
  if (storageError) {
    console.error('[DailyLog] Storage upload failed:', storageError);
    return { photo: null, error: `Storage upload failed: ${storageError.message}` };
  }

  const { data: urlData } = supabase.storage
    .from(bucket)
    .getPublicUrl(path);

  const payload = {
    project_id: cleanProjectId,
    file_name: file.name,
    file_url: urlData.publicUrl,
    file_size: String(file.size),
    file_type: ext,
    log_date: logDate,
    uploaded_context: 'daily_log',
    review_status: 'pending_management_review',
    is_client_visible: false,
    is_visible: true,
    is_deleted: false,
    caption: options?.caption || null,
    stage_id: cleanStageId || null,
    uploaded_by: userId,
    note: options?.caption || null,
  };

  const { data, error: dbError } = await supabase
    .from('jphph_project_files')
    .insert(payload)
    .select()
    .maybeSingle();

  if (dbError) {
    console.error('[DailyLog] Insert failed:', dbError);
    return { photo: null, error: `Database save failed: ${dbError.message}` };
  }

  // Debug logging for storage upload
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('Storage Upload Result', {
      bucket,
      path,
      publicUrl: urlData.publicUrl,
      stageId: cleanStageId,
      dbSaved: !!data && !dbError,
      dbError: null,
      fileName: file.name,
      fileSize: file.size,
    });
  }

  if (data && options?.dailyLogId) {
    const fileId = (data as { id: string }).id;
    const linkResult = await linkProjectFileToDailyLog({
      dailyLogId: options.dailyLogId,
      projectId: cleanProjectId,
      fileId,
      photoUrl: urlData.publicUrl,
      caption: options?.caption || null,
      uploadedBy: userId,
    });
    if (linkResult.error) {
      console.error('[DailyLog] daily log link failed:', linkResult.error);
      return {
        photo: null,
        error: `Photo uploaded but could not attach to daily log: ${linkResult.error}`,
      };
    }
  }

  if (data) {
    emitDailyLogSync(cleanProjectId);
  }

  // Notify management that a subcontractor uploaded a photo
  if (data) {
    // Get project management users to notify
    const { data: members } = await supabase
      .from('jphph_project_members')
      .select('user_id')
      .eq('project_id', cleanProjectId)
      .eq('member_role', 'management');

    const { data: project } = await supabase
      .from('jphph_projects')
      .select('project_name')
      .eq('id', cleanProjectId)
      .maybeSingle();

    if (members && members.length > 0) {
      for (const member of members) {
        await createNotification({
          user_id: member.user_id,
          project_id: cleanProjectId,
          project_name: (project as { project_name: string } | null)?.project_name || null,
          notification_type: 'photo',
          title: 'New daily log photo uploaded',
          body: `A subcontractor uploaded a new photo for ${logDate}. Review it in the Daily Log.`,
          target_role: 'management',
          link_path: '/management/daily-log',
          related_table: 'jphph_project_files',
          related_id: (data as { id: string }).id,
          created_by: userId,
        });
      }
    }
  }

  return { photo: data as DailyLogPhoto };
}
